import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Checkbox, Input, Icon, Loader, Dimmer } from 'semantic-ui-react';
import SusuModal from '../common/SusuModal';
import './FolderManagementModal.css';

/**
 * Reusable folder management modal for organizing items into folders.
 * Used by both AddToClosetModal (clothing items) and SaveToLookbookModal (looks/VTO results).
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {Array} props.folders - Array of folder objects with id, name, isDefault, itemCount, previewImage, itemIds
 * @param {Array} props.selectedItemIds - IDs of items being managed
 * @param {boolean} props.isLoading - Loading state for folders
 * @param {Function} props.onCreateFolder - Async function to create folder, returns new folder
 * @param {Function} props.onUpdateFolder - Async function to update folder name
 * @param {Function} props.onAddItems - Async function to add items to folder
 * @param {Function} props.onRemoveItems - Async function to remove items from folder
 * @param {Function} props.onSuccess - Optional callback after successful save
 * @param {string} props.title - Modal title (default: "Manage Folders")
 * @param {string} props.folderIcon - Icon name (default: "folder open")
 * @param {string} props.itemLabel - Label for folders (default: "folder")
 * @param {string} props.createPlaceholder - Placeholder for create input (default: "New folder name...")
 */
const FolderManagementModal = ({
  open,
  onClose,
  folders = [],
  selectedItemIds = [],
  isLoading = false,
  onCreateFolder,
  onUpdateFolder,
  onAddItems,
  onRemoveItems,
  onSuccess,
  title = 'Manage Folders',
  folderIcon = 'folder open',
  itemLabel = 'folder',
  createPlaceholder = 'New folder name...'
}) => {
  // Internal state
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [initialFolders, setInitialFolders] = useState([]);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Sort folders: default first, then newest to oldest (by ID descending)
  const sortedFolders = useMemo(() => {
    return [...folders].sort((a, b) => {
      if (a.isDefault) return -1;
      if (b.isDefault) return 1;
      return b.id - a.id;
    });
  }, [folders]);

  // Pre-select folders that already contain ALL selected items
  useEffect(() => {
    if (open && folders.length > 0 && selectedItemIds.length > 0) {
      const preSelected = folders
        .filter(folder => {
          const folderItemIds = folder.itemIds || [];
          return selectedItemIds.every(id => folderItemIds.includes(id));
        })
        .map(f => f.id);
      setSelectedFolders(preSelected);
      setInitialFolders(preSelected);
    }
  }, [open, folders, selectedItemIds]);

  // Calculate changes
  const changes = useMemo(() => {
    const toAdd = selectedFolders.filter(id => !initialFolders.includes(id));
    const toRemove = initialFolders.filter(id => !selectedFolders.includes(id));
    return { toAdd, toRemove };
  }, [selectedFolders, initialFolders]);

  const hasChanges = changes.toAdd.length > 0 || changes.toRemove.length > 0;


  // Handle folder toggle
  const handleFolderToggle = useCallback((folderId, isDefault) => {
    if (editingFolderId) return; // Don't toggle while editing
    if (isDefault) return; // Can't toggle default folder
    setSelectedFolders(prev =>
      prev.includes(folderId)
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  }, [editingFolderId]);

  // Handle create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !onCreateFolder) return;

    setIsCreating(true);
    setError(null);
    try {
      const result = await onCreateFolder(newFolderName.trim());
      // Auto-select newly created folder
      if (result?.id) {
        setSelectedFolders(prev => [...prev, result.id]);
      }
      setNewFolderName('');
    } catch (err) {
      console.error('Failed to create folder:', err);
      setError(err.message || 'Failed to create folder');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle start edit
  const handleStartEdit = (e, folder) => {
    e.stopPropagation();
    if (folder.isDefault) return; // Can't edit default folder name
    setEditingFolderId(folder.id);
    setEditingName(folder.name);
  };

  // Handle save edit
  const handleSaveEdit = async (e) => {
    e?.stopPropagation();
    if (!editingName.trim() || !editingFolderId || !onUpdateFolder) return;

    try {
      await onUpdateFolder(editingFolderId, editingName.trim());
      setEditingFolderId(null);
      setEditingName('');
    } catch (err) {
      console.error('Failed to update folder:', err);
      setError(err.message || 'Failed to update folder');
    }
  };

  // Handle cancel edit
  const handleCancelEdit = (e) => {
    e?.stopPropagation();
    setEditingFolderId(null);
    setEditingName('');
  };

  // Handle save all changes
  const handleSave = async () => {
    if (!hasChanges || selectedItemIds.length === 0) return;

    setIsSaving(true);
    setError(null);
    try {
      const promises = [];

      // Add items to newly selected folders
      if (onAddItems) {
        changes.toAdd.forEach(folderId => {
          promises.push(onAddItems(folderId, selectedItemIds));
        });
      }

      // Remove items from deselected folders
      if (onRemoveItems) {
        changes.toRemove.forEach(folderId => {
          promises.push(onRemoveItems(folderId, selectedItemIds));
        });
      }

      await Promise.all(promises);

      // Show success message briefly then close
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
        if (onSuccess) onSuccess();
      }, 1200);
    } catch (err) {
      console.error('Failed to save changes:', err);
      setError(err.message || 'Failed to save changes');
      setIsSaving(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setSelectedFolders([]);
    setInitialFolders([]);
    setNewFolderName('');
    setEditingFolderId(null);
    setEditingName('');
    setShowSuccess(false);
    setIsSaving(false);
    setError(null);
    onClose();
  };

  // Build summary text
  const getSummaryText = () => {
    const parts = [];
    if (changes.toAdd.length > 0) {
      parts.push(`Adding to ${changes.toAdd.length} ${itemLabel}${changes.toAdd.length !== 1 ? 's' : ''}`);
    }
    if (changes.toRemove.length > 0) {
      parts.push(`Removing from ${changes.toRemove.length} ${itemLabel}${changes.toRemove.length !== 1 ? 's' : ''}`);
    }
    if (parts.length === 0) {
      return 'No changes';
    }
    return parts.join(' • ');
  };


  return (
    <SusuModal
      open={open}
      onClose={handleClose}
      size="tiny"
      className="folder-management-modal"
      closeIcon
    >
      <SusuModal.Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon name={folderIcon} style={{ margin: 0 }} />
          <span>{title}</span>
        </div>
      </SusuModal.Header>
      <SusuModal.Content scrolling>
        <Dimmer active={isSaving || showSuccess} inverted>
          {showSuccess ? (
            <div className="success-content">
              <Icon name="check circle" size="huge" color="green" />
              <p>Saved successfully!</p>
            </div>
          ) : (
            <Loader>Saving...</Loader>
          )}
        </Dimmer>

        {/* Error message */}
        {error && (
          <div className="error-message">
            <Icon name="warning sign" color="red" />
            {error}
          </div>
        )}

        {/* Create new folder */}
        <div className="create-folder-section">
          <Input
            placeholder={createPlaceholder}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            action={{
              color: 'teal',
              icon: 'plus',
              onClick: handleCreateFolder,
              loading: isCreating,
              disabled: !newFolderName.trim() || isCreating
            }}
            fluid
          />
        </div>

        {/* Folder list */}
        <div className="folder-list">
          {isLoading ? (
            <Dimmer active inverted>
              <Loader size="small">Loading...</Loader>
            </Dimmer>
          ) : folders.length === 0 ? (
            <p className="no-folders">No {itemLabel}s yet. Create one above!</p>
          ) : (
            sortedFolders.map(folder => {
              const folderItemIds = folder.itemIds || [];
              const selectedInFolder = selectedItemIds.filter(id => folderItemIds.includes(id)).length;
              const hasPartial = selectedInFolder > 0 && selectedInFolder < selectedItemIds.length;
              const isChecked = selectedFolders.includes(folder.id);
              const wasInitial = initialFolders.includes(folder.id);
              const willAdd = isChecked && !wasInitial;
              const willRemove = !isChecked && wasInitial;
              const isEditing = editingFolderId === folder.id;

              return (
                <div
                  key={folder.id}
                  className={`folder-item ${isChecked ? 'selected' : ''} ${willAdd ? 'will-add' : ''} ${willRemove ? 'will-remove' : ''} ${folder.isDefault ? 'default-folder' : ''}`}
                  onClick={() => handleFolderToggle(folder.id, folder.isDefault)}
                >
                  <Checkbox
                    checked={isChecked || folder.isDefault}
                    disabled={folder.isDefault}
                    indeterminate={hasPartial && !isChecked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => handleFolderToggle(folder.id, folder.isDefault)}
                  />
                  
                  {/* Preview image */}
                  <div className="folder-preview">
                    {folder.previewImage ? (
                      <img src={folder.previewImage} alt={folder.name} />
                    ) : (
                      <Icon name={folderIcon} size="large" />
                    )}
                  </div>

                  {isEditing ? (
                    <div className="edit-name-wrapper" onClick={e => e.stopPropagation()}>
                      <Input
                        size="mini"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit(e)}
                        onKeyDown={(e) => e.key === 'Escape' && handleCancelEdit(e)}
                        autoFocus
                      />
                      <Icon name="check" color="green" link onClick={handleSaveEdit} />
                      <Icon name="close" color="grey" link onClick={handleCancelEdit} />
                    </div>
                  ) : (
                    <span className="folder-name">
                      {folder.name}
                      {folder.isDefault && <span className="default-badge">default</span>}
                      {!folder.isDefault && (
                        <Icon
                          name="pencil"
                          size="small"
                          className="edit-icon"
                          onClick={(e) => handleStartEdit(e, folder)}
                        />
                      )}
                      {willAdd && <span className="change-badge add">+add</span>}
                      {willRemove && <span className="change-badge remove">-remove</span>}
                    </span>
                  )}
                  <span className="item-count">
                    {hasPartial ? `${selectedInFolder}/${selectedItemIds.length}` : `${folder.itemCount || 0}`}
                  </span>
                </div>
              );
            })
          )}
        </div>

        <p className="selection-info">{getSummaryText()}</p>
      </SusuModal.Content>
      <SusuModal.Actions>
        <Button basic onClick={handleClose} disabled={isSaving}>Cancel</Button>
        <Button
          color="teal"
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
        >
          <Icon name="check" /> Save
        </Button>
      </SusuModal.Actions>
    </SusuModal>
  );
};

export default FolderManagementModal;
